import { useMemo } from 'react';
import useGenre from './useGenre';
import useGenreUrlParams from './useGenreUrlParams';
import { map } from '@/helpers/lodash';
import useGenres, { GENRES_ALL_FLATTENED } from '@/react-query/genres/hooks/useGenres';
import { subgenreTitleWithParentTitleStripped } from '@/helpers/pages/genres';
import pageKeys from '@/helpers/routing/constants';
import useLanguage from '@/hooks/language/useLanguage';

const SELECTOR_ID_ALL = 'all';
const SELECTOR_ID_NEW = 'new';
const GENRE_OPTION_ALL = {
  id: SELECTOR_ID_ALL,
  value: 'All'
};
const GENRE_OPTION_NEW = {
  id: SELECTOR_ID_NEW,
  value: 'New',
  i18nNamespace: 'common'
};

const useGenreSelectorOptions = () => {
  const { resolvedLanguage } = useLanguage();
  const { genreSlug, isViewingNewShows } = useGenreUrlParams();
  const {
    parentGenre,
    isLoading
  } = useGenre();
  const { genres } = useGenres({ type: GENRES_ALL_FLATTENED });

  const {
    title: parentTitle,
    slug: parentSlug
  } = parentGenre;
  
  // Create the drop down options for the mobile genre selector
  const genreOptions = useMemo(() => map(genres, ({
    id: genreOptionId, title: gTitle, slug
  }) => ({
    id: genreOptionId,
    value: subgenreTitleWithParentTitleStripped({
      parentTitle,
      subgenreTitle: gTitle
    }),
    href: pageKeys.genre(slug)
  })).filter((g) => (genres?.[g.id]?.parent || {})?.slug === parentSlug), [genres, parentSlug, parentTitle]);

  // Find the genre in the list that corresponds with the one in the route
  let selectedGenre;
  if (isViewingNewShows) {
    selectedGenre = GENRE_OPTION_NEW;
  } else if (parentSlug === genreSlug) {
    selectedGenre = GENRE_OPTION_ALL;
  } else {
    selectedGenre = genreOptions.find((g) => genres[g.id].slug === genreSlug) || {};
  }
  const selectorOptions = useMemo(
    () => (
      (isLoading || !resolvedLanguage)
        ? []
        : [
          {
            ...GENRE_OPTION_ALL,
            href: pageKeys.genre(parentSlug),
            value: GENRE_OPTION_ALL.value
          },
          {
            ...GENRE_OPTION_NEW,
            href: pageKeys.genre(parentSlug, true),
            value: GENRE_OPTION_NEW.value
          },
          ...genreOptions
        ]
    ),
    [isLoading, genreOptions, parentSlug, resolvedLanguage]
  );

  return {
    selectedGenre,
    selectorOptions
  };
};

export default useGenreSelectorOptions;
